/** Tasks — list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import type { Task } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { StatusBadge } from "@/components/app/StatusBadge";
import { RefLink } from "@/components/inspector";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "../projects/_shared";

export function TaskList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deleteTask } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<Task | null>(null);

  const columns = useMemo<ColumnDef<Task, unknown>[]>(
    () => [
      {
        id: "instruction",
        accessorKey: "instruction",
        header: "Instruction",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="line-clamp-1 max-w-md font-medium">
            {row.original.instruction ?? "—"}
          </span>
        ),
      },
      {
        id: "task_status",
        accessorKey: "task_status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.task_status} />,
      },
      {
        id: "artifact_id",
        accessorKey: "artifact_id",
        header: "Artifact",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink resource="artifacts" id={row.original.artifact_id} />
          </span>
        ),
      },
      {
        id: "assignee_user_id",
        accessorKey: "assignee_user_id",
        header: "Assignee",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.assignee_user_id ?? "—"}
          </span>
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) =>
          new Date(row.original.created_at).toLocaleDateString(),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => go({ to: editUrl("tasks", row.original.id) })}
            onShow={() => go({ to: showUrl("tasks", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<Task, HttpError>({
    columns,
    refineCoreProps: {
      resource: "tasks",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tasks"
        description="Executable work items linked to artifacts."
        action={
          <Button onClick={() => go({ to: createUrl("tasks") })}>
            <Icon name="create" size={16} className="mr-2" aria-hidden /> New Task
          </Button>
        }
      />

      <SearchBox value={search} onChange={setSearch} placeholder="Search tasks…" />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No tasks yet. Create your first task."
        onRowClick={(t) => go({ to: showUrl("tasks", t.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete task?"
        description={
          <>
            This permanently deletes{" "}
            <span className="font-medium">
              {pendingDelete?.instruction
                ? `“${pendingDelete.instruction.slice(0, 60)}${
                    pendingDelete.instruction.length > 60 ? "…" : ""
                  }”`
                : "this task"}
            </span>
            . This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteTask({ resource: "tasks", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

function RowActions({
  onEdit,
  onShow,
  onDelete,
}: {
  onEdit: () => void;
  onShow: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Icon name="more" size={16} aria-hidden />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <Icon name="edit" size={16} className="mr-2" aria-hidden /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Icon name="delete" size={16} className="mr-2" aria-hidden /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default TaskList;
